import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useTenant } from '@emerald-works-nova/auth'
import { useStyles } from './style'
import { Box, Typography } from '@mui/material'
import { Help } from '@mui/icons-material'
import Gleap from 'gleap'
import consts from '../../consts'
import { useMindset } from '../mindset-agent/mindset-context'
import { useGleap } from './gleap-context'
import { useLocation } from 'react-router-dom'
import { userSlice } from '../../reducers'

const GLEAP_BUBBLE_ROUTES = ['/signup', '/profile']

const GleapBubble = () => {
  const classes = useStyles()
  const tenant = useTenant()
  const location = useLocation()
  const { isOpen: isMindsetAgentOpen } = useMindset()
  const { handleOpen } = useGleap()
  const profile = useSelector(userSlice.selectors.selectProfile)
  const isClubTenant = tenant.pk === consts.DEFAULT_CLUB_PK
  const isGleapEnabled = process.env.REACT_APP_GLEAP_ENABLED === 'true'
  const wasOpenRef = useRef(false)
  const previousRouteWasIncludedRef = useRef(false)

  // Check if current route should show Gleap bubble
  const shouldShowBubble = GLEAP_BUBBLE_ROUTES.some(route => location.pathname.startsWith(route))

  // Track widget open state only on included routes
  useEffect(() => {
    // Only track open state if we're on an included route
    if (!shouldShowBubble) {
      return
    }

    const checkOpenState = async () => {
      try {
        const isOpen = await Gleap.isOpened()
        wasOpenRef.current = isOpen
      } catch (error) {
        // Gleap might not be initialized yet
      }
    }

    checkOpenState()
    const interval = setInterval(checkOpenState, 500)
    return () => clearInterval(interval)
  }, [shouldShowBubble])

  // If the route is not the Signup route, call Gleap.identify with the user's email
  useEffect(() => {
    if (profile) {
      const userId = `${consts.DEFAULT_CLUB_PK}#${profile.username}`
      const payload = {
        name: `${profile.givenName} ${profile.familyName}`,
        email: profile.email
      }
      Gleap.identify(userId, payload)
    }
  }, [profile])

  // Handle widget state on navigation
  useEffect(() => {
    const handleNavigation = async () => {
      try {
        const isCurrentlyOpen = await Gleap.isOpened()

        // If navigating FROM an authorized route TO a non-authorized route, close the widget
        if (previousRouteWasIncludedRef.current && !shouldShowBubble && isCurrentlyOpen) {
          Gleap.close()
          wasOpenRef.current = false
        }

        // If navigating between authorized routes, restore the widget state
        if (previousRouteWasIncludedRef.current && shouldShowBubble && wasOpenRef.current && !isMindsetAgentOpen) {
          if (!isCurrentlyOpen) {
            Gleap.open()
          }
        }

        // Update previous route tracking after handling navigation
        previousRouteWasIncludedRef.current = shouldShowBubble
      } catch (error) {
        // Gleap might not be ready
        // Still update the ref even on error
        previousRouteWasIncludedRef.current = shouldShowBubble
      }
    }

    // Small delay to let route change complete
    const timer = setTimeout(handleNavigation, 100)
    return () => clearTimeout(timer)
  }, [location.pathname, shouldShowBubble, isMindsetAgentOpen])

  // Reset widget restoration tracking when Mindset Agent opens or when leaving included routes
  useEffect(() => {
    if (isMindsetAgentOpen || !shouldShowBubble) {
      wasOpenRef.current = false
    }
  }, [isMindsetAgentOpen, shouldShowBubble])

  // Determine if we should render the bubble UI
  const shouldRenderBubble = isClubTenant && isGleapEnabled && shouldShowBubble && !isMindsetAgentOpen

  // Early return only if Gleap is completely disabled (component won't be needed)
  if (!isClubTenant || !isGleapEnabled) return null

  // Keep component mounted even on non-authorized routes to handle widget closing
  // but don't render the bubble UI
  if (!shouldRenderBubble) return <></>

  return (
    <Box
      className={classes.gleapBubble}
      onClick={handleOpen}
      data-test='gleap-bubble'
    >
      <Help fontSize='medium' />
      <Typography variant='body2' textAlign='center' fontSize='16px'>Help</Typography>
    </Box>
  )
}

export default GleapBubble
